import React from 'react';
import { ResultadoConsolidado } from '../../types/Calculadora';
import { formatCurrency, formatNumber, formatPct } from './calculadoraHelpers';
import { Badge, Card } from '../ui';

interface Props {
  consolidado: ResultadoConsolidado;
  /** Salta a la sub-pestaña de ese plan. */
  onVerPlan: (planId: string) => void;
}

const ConsolidadoMix: React.FC<Props> = ({ consolidado, onVerPlan }) => {
  const filas = consolidado.porPlan;

  return (
    <Card className="p-6">
      <h2 className="text-base font-semibold text-gappex-secondary mb-1">Resultado por plan</h2>
      <p className="text-xs text-gray-400 mb-4">
        Los planes destildados quedan fuera del consolidado pero se siguen mostrando en gris. Los
        ficticios se calculan igual que los reales.
      </p>

      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase border-b">
              <th className="pb-2 pr-4">Plan</th>
              <th className="pb-2 pr-4 text-right">Suscriptores</th>
              <th className="pb-2 pr-4 text-right">Cuota prom.</th>
              <th className="pb-2 pr-4 text-right">Ingreso anual</th>
              <th className="pb-2 pr-4 text-right">Gasto prom.</th>
              <th className="pb-2 pr-4 text-right">Resultado</th>
              <th className="pb-2 text-right">Margen</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((plan) => {
              const positivo = plan.resultadoAnualProm >= 0;
              return (
                <tr
                  key={plan.planId}
                  className={`border-b last:border-0 hover:bg-slate-50/70 transition-colors duration-100 ${
                    plan.incluido ? '' : 'opacity-40'
                  }`}
                >
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onVerPlan(plan.planId)}
                        className="font-medium text-gappex-secondary hover:text-gappex-primary hover:underline transition-colors text-left"
                      >
                        {plan.nombre}
                      </button>
                      {plan.ficticio && (
                        <Badge tone="info" title="Plan inventado a mano: no está en el catálogo">
                          Ficticio
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {formatNumber(plan.suscriptores)}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums text-gray-500">
                    {formatCurrency(plan.cuotaPromedio)}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {formatCurrency(plan.ingresoAnual)}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums text-amber-600">
                    {formatCurrency(plan.gastoAnualProm)}
                  </td>
                  <td
                    className={`py-2 pr-4 text-right tabular-nums font-semibold ${
                      positivo ? 'text-emerald-600' : 'text-red-500'
                    }`}
                  >
                    {formatCurrency(plan.resultadoAnualProm)}
                  </td>
                  <td
                    className={`py-2 text-right tabular-nums ${
                      positivo ? 'text-emerald-600' : 'text-red-500'
                    }`}
                  >
                    {plan.ingresoAnual > 0 ? formatPct(plan.margenPct) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-semibold text-gappex-secondary border-t-2">
              <td className="pt-3 pr-4">Consolidado</td>
              <td className="pt-3 pr-4 text-right tabular-nums">
                {formatNumber(consolidado.suscriptores)}
              </td>
              <td className="pt-3 pr-4" />
              <td className="pt-3 pr-4 text-right tabular-nums">
                {formatCurrency(consolidado.ingresoAnual)}
              </td>
              <td className="pt-3 pr-4 text-right tabular-nums text-amber-700">
                {formatCurrency(consolidado.gastoAnualProm)}
              </td>
              <td
                className={`pt-3 pr-4 text-right tabular-nums ${
                  consolidado.resultadoAnualProm >= 0 ? 'text-emerald-700' : 'text-red-600'
                }`}
              >
                {formatCurrency(consolidado.resultadoAnualProm)}
              </td>
              <td
                className={`pt-3 text-right tabular-nums ${
                  consolidado.resultadoAnualProm >= 0 ? 'text-emerald-700' : 'text-red-600'
                }`}
              >
                {consolidado.ingresoAnual > 0 ? formatPct(consolidado.margenPct) : '—'}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
};

export default ConsolidadoMix;
